// Blobs: pictures and files kept once on the server by the sha256 of their
// bytes (POST /api/blobs) and named by id where they are used (docs/decisions.md
// "Pictures and files are blobs, referenced by id"). A message's pictures ride
// the synced session as ids, never as base64, so they cost neither the
// changelog nor every device. Pure: the upload itself is passed in.

/**
 * Phase 2 of the blob store. Phase 1 (this build) reads, checks and draws blob
 * pictures everywhere but still sends pictures inline, because the build before
 * it rewrites a thread without the `blob` field it does not know (a rolling
 * deploy or a rollback would strip them). On since phase 2 (2026-10-06), released
 * only after phase 1 was in prod: `api.agentSend` uploads each picture first. Never
 * release a build older than phase 1 (cba48132d65e): it would strip references.
 */
export const UPLOAD_PICTURES_AS_BLOBS = true

/** A blob id: the lowercase hex sha256 of the bytes (store.ValidBlobID). */
export const BLOB_ID = /^[0-9a-f]{64}$/

/** What POST /api/blobs answers. */
export interface BlobRef {
  id: string
  media_type: string
  size: number
}

/** A picture the composer made: base64, no data: prefix. */
export interface InlinePicture {
  media_type: string
  data: string
}

/** A picture as a message carries it now: a blob id. */
export interface BlobPicture {
  media_type: string
  blob: string
}

/** A picture on a stored message: a blob id, or base64 from a client older than the blob store. */
export type SessionPicture = { media_type: string; data?: string; blob?: string }

/** The bytes of a base64 string, as a Blob of the given type. */
export const base64Blob = (data: string, type: string): Blob => {
  const bin = atob(data)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type })
}

/**
 * The pictures to send: each inline picture uploaded as a blob first and sent
 * by id; one already a blob is sent as it is. Any failed upload throws, so the
 * sender keeps its draft and nothing half-sent lands on the thread.
 */
export const uploadPictures = async (
  images: (InlinePicture | BlobPicture)[],
  put: (b: Blob) => Promise<BlobRef>,
): Promise<BlobPicture[]> =>
  Promise.all(
    images.map(async (im) => {
      if ('blob' in im) return { media_type: im.media_type, blob: im.blob }
      const ref = await put(base64Blob(im.data, im.media_type))
      if (!BLOB_ID.test(ref.id)) throw new Error('The picture could not be kept')
      return { media_type: im.media_type, blob: ref.id }
    }),
  )

/**
 * The pictures a message is sent with: uploaded and sent by id once
 * UPLOAD_PICTURES_AS_BLOBS is on (phase 2), else inline as they are (phase 1).
 */
export const picturesToSend = async (
  images: InlinePicture[],
  put: (b: Blob) => Promise<BlobRef>,
  asBlobs: boolean = UPLOAD_PICTURES_AS_BLOBS,
): Promise<(InlinePicture | BlobPicture)[]> => (asBlobs ? uploadPictures(images, put) : images)

/** How a stored picture is drawn: inline data straight away, a blob once fetched (null until then). */
export const pictureSource = (p: SessionPicture, urls: Record<string, string>): string | null => {
  if (p.blob) return urls[p.blob] ?? null
  if (p.data) return `data:${p.media_type};base64,${p.data}`
  return null
}
